import { useEffect, useMemo, useState } from "react";
import { divIcon } from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { MapPin, Navigation } from "lucide-react";
import { DEFAULT_MAP_CENTER } from "../../shops/constants";
import type { Shop } from "../../shops/types";
import { hasExactLocation } from "../../shops/utils";
import { displayValue } from "../../../shared/utils/format";

type ShopsMapProps = {
  shops: Shop[];
  selectedShopId: string | null;
  onSelectShop: (shop: Shop) => void;
};

type ShopCluster = {
  id: string;
  latitude: number;
  longitude: number;
  shops: Shop[];
};

function clusterCellSize(zoom: number): number {
  if (zoom < 7) {
    return 1.4;
  }

  if (zoom < 10) {
    return 0.45;
  }

  if (zoom < 13) {
    return 0.12;
  }

  return 0.025;
}

function clusterShops(shops: Shop[], zoom: number): ShopCluster[] {
  const cellSize = clusterCellSize(zoom);
  const buckets = new Map<string, Shop[]>();

  for (const shop of shops) {
    const lat = shop.shopLocation.latitude!;
    const lng = shop.shopLocation.longitude!;
    const key = `${Math.round(lat / cellSize)}:${Math.round(lng / cellSize)}`;
    buckets.set(key, [...(buckets.get(key) ?? []), shop]);
  }

  return Array.from(buckets.entries()).map(([id, bucket]) => {
    const latitude =
      bucket.reduce((total, shop) => total + shop.shopLocation.latitude!, 0) / bucket.length;
    const longitude =
      bucket.reduce((total, shop) => total + shop.shopLocation.longitude!, 0) / bucket.length;

    return {
      id,
      latitude,
      longitude,
      shops: bucket
    };
  });
}

function MapZoomTracker({
  onZoomChange
}: {
  onZoomChange: (zoom: number) => void;
}) {
  const map = useMapEvents({
    zoomend() {
      onZoomChange(map.getZoom());
    }
  });

  return null;
}

function FlyToSelected({ shop }: { shop: Shop | null }) {
  const map = useMap();

  useEffect(() => {
    if (shop && hasExactLocation(shop.shopLocation)) {
      map.flyTo([shop.shopLocation.latitude!, shop.shopLocation.longitude!], 15, {
        duration: 0.8
      });
    }
  }, [shop, map]);

  return null;
}

export function ShopsMap({ shops, selectedShopId, onSelectShop }: ShopsMapProps) {
  const locatedShops = useMemo(() => shops.filter((shop) => hasExactLocation(shop.shopLocation)), [shops]);
  const [zoom, setZoom] = useState(7);
  const clusters = useMemo(() => clusterShops(locatedShops, zoom), [locatedShops, zoom]);
  const selectedShop = locatedShops.find((shop) => shop.id === selectedShopId) ?? null;

  const shopIcon = useMemo(
    () =>
      divIcon({
        className: "field-marker shop-marker",
        html: "<span></span>",
        iconSize: [26, 26],
        iconAnchor: [13, 13]
      }),
    []
  );

  const selectedIcon = useMemo(
    () =>
      divIcon({
        className: "field-marker shop-marker selected-marker",
        html: "<span></span>",
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      }),
    []
  );

  const clusterIcon = (count: number) =>
    divIcon({
      className: "cluster-marker",
      html: `<span>${count}</span>`,
      iconSize: [38, 38],
      iconAnchor: [19, 19]
    });

  const center =
    locatedShops.length > 0
      ? [locatedShops[0].shopLocation.latitude!, locatedShops[0].shopLocation.longitude!]
      : [DEFAULT_MAP_CENTER.latitude, DEFAULT_MAP_CENTER.longitude];

  return (
    <section className="map-view">
      <div className="map-toolbar">
        <div>
          <p className="eyebrow">Map view</p>
          <h2>{locatedShops.length} shops with exact location</h2>
        </div>
        <div className="map-stat">
          <Navigation size={16} />
          {shops.length - locatedShops.length} missing location
        </div>
      </div>

      <div className="map-frame full-map">
        <MapContainer center={center as [number, number]} zoom={7} scrollWheelZoom className="leaflet-map">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapZoomTracker onZoomChange={setZoom} />
          <FlyToSelected shop={selectedShop} />

          {clusters.map((cluster) => {
            if (cluster.shops.length > 1) {
              return (
                <Marker
                  key={cluster.id}
                  icon={clusterIcon(cluster.shops.length)}
                  position={[cluster.latitude, cluster.longitude]}
                >
                  <Popup>
                    <div className="popup-list">
                      <strong>{cluster.shops.length} shops nearby</strong>
                      {cluster.shops.slice(0, 8).map((shop) => (
                        <button type="button" key={shop.id} onClick={() => onSelectShop(shop)}>
                          {shop.shopName}
                        </button>
                      ))}
                    </div>
                  </Popup>
                </Marker>
              );
            }

            const shop = cluster.shops[0];
            const isSelected = shop.id === selectedShopId;

            return (
              <Marker
                key={shop.id}
                icon={isSelected ? selectedIcon : shopIcon}
                position={[shop.shopLocation.latitude!, shop.shopLocation.longitude!]}
                eventHandlers={{
                  click() {
                    onSelectShop(shop);
                  }
                }}
              >
                <Popup>
                  <div className="shop-popup">
                    <strong>{shop.shopName}</strong>
                    <span>{displayValue(shop.shopPhoneNumber, "No phone")}</span>
                    <span>
                      {displayValue(shop.shopClass, "No class")} · {displayValue(shop.shopCity)}
                    </span>
                    <span>{displayValue(shop.shopDistrict)}</span>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {locatedShops.length === 0 ? (
          <div className="map-empty">
            <MapPin size={24} />
            Add a shop with a map location to see markers here.
          </div>
        ) : null}
      </div>
    </section>
  );
}
